import createSocket from '@/utils/socket';

// Tasks whose active state drives the per-model "recalculating" spinner.
// Kept in sync with RECALCULATION_TASKS in api/app/controllers/jobs.py.
const RECALCULATION_TASKS = ['calculate_conf_matrix_items', 'calculate_performance_job'];

function projectJobsChannel(projectId) {
  if (!projectId || projectId === 'undefined') {
    return null;
  }
  return `${projectId}_jobs`;
}

function parseJobState(job) {
  if (!job || !job.description) {
    return '';
  }
  try {
    const desc = typeof job.description === 'string'
      ? JSON.parse(job.description)
      : job.description;
    if (desc && desc.eval_metrics && desc.eval_metrics.label) {
      return String(desc.eval_metrics.label);
    }
    if (desc && desc.state) {
      return String(desc.state);
    }
  } catch {
    /* ignore */
  }
  return '';
}

function taskDefaultLabel(task) {
  if (task === 'calculate_conf_matrix_items') {
    return 'Evaluating · analysing results';
  }
  if (task === 'calculate_performance_job') {
    return 'Evaluating · computing performance';
  }
  return 'Evaluating';
}

const state = {
  socket: null,
  channel: null,
  // { [modelId]: { [task]: { progress, state } } }
  modelTasks: {},
};

const getters = {
  is_model_recalculating: (state) => (modelId) => {
    const tasks = state.modelTasks[modelId];
    return !!tasks && Object.keys(tasks).length > 0;
  },
  model_evaluation_status: (state) => (modelId) => {
    const tasks = state.modelTasks[modelId];
    if (!tasks || Object.keys(tasks).length === 0) {
      return null;
    }
    const order = ['calculate_conf_matrix_items', 'calculate_performance_job'];
    for (const task of order) {
      if (tasks[task]) {
        return {
          task,
          progress: Number(tasks[task].progress) || 0,
          state: tasks[task].state || taskDefaultLabel(task),
        };
      }
    }
    const task = Object.keys(tasks)[0];
    return {
      task,
      progress: Number(tasks[task].progress) || 0,
      state: tasks[task].state || taskDefaultLabel(task),
    };
  },
  /** First active evaluation status among model ids (for a train config row). */
  evaluation_status_for_models: (state, getters) => (modelIds) => {
    const ids = (modelIds || []).map((id) => Number(id)).filter(Boolean);
    for (const id of ids) {
      const status = getters.model_evaluation_status(id);
      if (status) {
        return { modelId: id, ...status };
      }
    }
    return null;
  },
};

const mutations = {
  set_socket(state, socket) {
    state.socket = socket;
  },
  set_channel(state, channel) {
    state.channel = channel;
  },
  reset_model_tasks(state) {
    state.modelTasks = {};
  },
  set_model_task_active(state, { modelId, task, progress = 0, state: jobState = '' }) {
    const existing = state.modelTasks[modelId] || {};
    const prev = existing[task] || {};
    state.modelTasks = {
      ...state.modelTasks,
      [modelId]: {
        ...existing,
        [task]: {
          progress: progress != null ? progress : (prev.progress || 0),
          state: jobState || prev.state || taskDefaultLabel(task),
        },
      },
    };
  },
  clear_model_task(state, { modelId, task }) {
    const existing = state.modelTasks[modelId];
    if (!existing || !(task in existing)) {
      return;
    }
    const next = { ...existing };
    delete next[task];
    const nextAll = { ...state.modelTasks };
    if (Object.keys(next).length === 0) {
      delete nextAll[modelId];
    } else {
      nextAll[modelId] = next;
    }
    state.modelTasks = nextAll;
  },
};

const actions = {
  // Idempotently subscribe to the current project's jobs websocket and load the
  // current recalculation state once. Safe to call from every chip on mount.
  async ensure_subscribed(context) {
    const projectId = sessionStorage.getItem('project_id');
    const channel = projectJobsChannel(projectId);
    if (!channel) {
      return;
    }
    if (context.state.channel === channel && context.state.socket) {
      return;
    }
    context.dispatch('teardown');
    context.commit('set_channel', channel);
    // Open the socket synchronously (before awaiting) so that other chips
    // mounting in the same tick see an active subscription and don't each
    // open their own connection.
    context.dispatch('setup_socket', channel);
    await context.dispatch('fetch_active');
  },
  async fetch_active(context) {
    try {
      const results = await Promise.all(
        RECALCULATION_TASKS.map((task) => this._vm.$api.job.get_active_jobs(task)),
      );
      results.forEach((jobs, idx) => {
        const task = RECALCULATION_TASKS[idx];
        (jobs || []).forEach((job) => {
          if (job && job.model_id && job.processed === 0) {
            context.commit('set_model_task_active', {
              modelId: job.model_id,
              task,
              progress: job.progress || 0,
              state: parseJobState(job) || taskDefaultLabel(task),
            });
          }
        });
      });
    } catch (error) {
      console.error('Failed to load recalculation status', error);
    }
  },
  setup_socket(context, channel) {
    const socket = createSocket();
    context.commit('set_socket', socket);
    socket.on('connect', () => {
      socket.emit('subscribe', channel);
    });
    socket.on('message', (message) => {
      let payload = null;
      try {
        payload = JSON.parse(JSON.parse(message));
      } catch (e) {
        return;
      }
      if (!payload || payload.type !== 'project_job_update' || !payload.job) {
        return;
      }
      context.dispatch('apply_job_update', payload.job);
    });
  },
  apply_job_update(context, job) {
    if (!job || !RECALCULATION_TASKS.includes(job.task) || !job.model_id) {
      return;
    }
    const done = job.deleted_at || job.processed === 1;
    if (done) {
      context.commit('clear_model_task', { modelId: job.model_id, task: job.task });
    } else {
      context.commit('set_model_task_active', {
        modelId: job.model_id,
        task: job.task,
        progress: job.progress || 0,
        state: parseJobState(job) || taskDefaultLabel(job.task),
      });
    }
  },
  teardown(context) {
    if (context.state.socket) {
      context.state.socket.close();
    }
    context.commit('set_socket', null);
    context.commit('set_channel', null);
    context.commit('reset_model_tasks');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
